import { ConfigStateService, LocalizationService } from '@abp/ng.core';
import { ToasterService } from '@abp/ng.theme.shared';
import {
  Component,
  OnInit,
  forwardRef,
  Input,
  Output,
  EventEmitter,
  ViewChild,
} from '@angular/core';
import { NgbDateStruct, NgbDateParserFormatter, NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import { NgbDatepickerI18n, NgbDatepicker } from '@ng-bootstrap/ng-bootstrap';
import { NgbTimeStruct } from '@ng-bootstrap/ng-bootstrap';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CustomDatepickerI18n } from 'src/core/localizations/datetime-picker/datepicker-i18n';
import { CADatePipe } from 'src/core/pipes/ca-date.pipe';
import { DateDisplayType } from 'src/ca-shared/conversation-date-filter/models/date-display-type.enum';
import { TimeZoneHelper } from 'src/core/services/helper/timezone-helper';
import { GlobalSettingsService } from 'src/core/services/settings/global-settings.service';

@Component({
  selector: 'ca-datetime-picker',
  templateUrl: './datetime-picker.component.html',
  styleUrls: ['./datetime-picker.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => DatetimePickerComponent),
    },
    { provide: NgbDatepickerI18n, useClass: CustomDatepickerI18n },
  ],
  standalone: false,
})
export class DatetimePickerComponent implements OnInit, ControlValueAccessor {
  _disable: boolean = false;
  _allowNull: boolean = false;
  isRTL: boolean;

  @ViewChild('dtp', { static: true, read: NgbDatepicker })
  dtp: NgbDatepicker;

  @ViewChild('dropDownPicker', { static: false, read: NgbDropdown })
  dropDownPicker: NgbDropdown;

  date: Date;
  @Input()
  minDate: Date;
  @Input()
  maxDate: Date;
  @Input()
  customClass: string;
  @Input()
  hideTime: boolean;
  @Input()
  useShortcuts: boolean;
  @Input()
  showInfiniteIcon: boolean;
  // When set, a date picked from the calendar gets this time instead of falling back to
  // 00:00, unless the user typed a time himself. Opt-in: consumers that do not set it
  // keep the previous behavior.
  @Input()
  defaultTimeOnDateSelect: NgbTimeStruct;
  // Optional IANA time zone id override (e.g. "Europe/Berlin") in which the picker interprets,
  // displays and DST-gap-resolves its values. When unset, the picker falls back to the tenant's
  // Global.SystemTimeZone, and only to the browser/OS time zone (this.localTimeZone) if that
  // setting is empty. See effectiveTimeZone.
  @Input()
  timeZone: string;
  @Output()
  changed: EventEmitter<{ id: number }> = new EventEmitter();

  private indefiniteLabel: string = '';
  private selectedShortcut: number = 0;
  private _timeEnteredByUser = false;
  private localTimeZone: string;

  get formatedMinDate(): NgbDateStruct {
    if (this.minDate) return this.getNgbStructDate(this.minDate);
    else return null;
  }
  get formatedMaxDate(): NgbDateStruct {
    if (this.maxDate) {
      return this.getNgbStructDate(this.maxDate);
    } else {
      return null;
    }
  }

  private get effectiveTimeZone(): string {
    return this.timeZone || this.globalSettingsService.systemTimeZone || this.localTimeZone;
  }

  get formatedClass(): string {
    const tempClass = 'ca-dropdown-menu';
    if (this.customClass) return this.customClass;
    else return tempClass;
  }

  @Input()
  set disabled(value: boolean) {
    this._disable = value;
  }

  get disabled(): boolean {
    return this._disable;
  }

  @Input()
  set allowNull(value: boolean) {
    this._allowNull = value;
  }

  get allowNull(): boolean {
    return this._allowNull;
  }

  ngbDate: NgbDateStruct;
  ngbTime: NgbTimeStruct;
  dateTime: string;
  constructor(
    private parserFormatter: NgbDateParserFormatter,
    private caDatePie: CADatePipe,
    private timeZoneHelper: TimeZoneHelper,
    private config: ConfigStateService,
    private localizationService: LocalizationService,
    private toasterService: ToasterService,
    private globalSettingsService: GlobalSettingsService
  ) {}

  ngOnInit(): void {
    const localize = this.config.getOne('localization');
    this.isRTL = localize?.currentCulture.cultureName === 'ar';
    this.indefiniteLabel = this.localizationService.instant('Category::EndDateIndefinite');
    this.localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  ngAfterViewInit() {}

  writeValue(date: Date, setNull: boolean = false): void {
    if (date) {
      if (typeof date === 'string') {
        date = new Date(date);
      }
      this.date = date;
    } else {
      if (!this.allowNull) {
        this.date = new Date();
      }
    }

    if (typeof this.minDate === 'string') {
      this.minDate = new Date(this.minDate);
    }
    if (typeof this.maxDate === 'string') {
      this.maxDate = new Date(this.maxDate);
    }

    if (this.date) {
      if (this.maxDate && this.date > this.maxDate) {
        this.date.setTime(this.maxDate.getTime());
      }

      if (this.minDate && this.date < this.minDate) {
        this.date.setTime(this.minDate.getTime());
      }

      this.ngbDate = this.getNgbStructDate(this.date);
      this.ngbTime = this.getNgbTimeStruct(this.date);
      this._timeEnteredByUser = false;
      this.setDateTime();
      this.applyChanges();
    }
  }
  resetValue() {
    this.date = null;
    this.ngbDate = null;
    this.ngbTime = null;
    this._timeEnteredByUser = false;
    this.dateTime = '';
    // Non-nullable pickers are also reset programmatically (e.g. category-date-filter); keep them silent.
    if (this.allowNull) {
      this.onChange(null);
      this.onTouched();
      this.changed.emit();
    }
  }
  registerOnChange(fn: Function): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: Function): void {
    this.onTouched = fn;
  }
  onChange: Function = (_: Date) => {};
  onTouched: Function = (_: Date) => {};
  onDateSelect(eventArgs) {
    this.ngbDate = eventArgs;
    if (this.defaultTimeOnDateSelect && !this._timeEnteredByUser) {
      // Copy so the consumer's object can never be mutated by the timepicker.
      this.ngbTime = { ...this.defaultTimeOnDateSelect };
    }
    if (!this.ngbTime) {
      // Empty or cleared picker: the picked date falls back to 00:00 in effectiveTimeZone.
      this.ngbTime = { hour: 0, minute: 0, second: 0 };
    }
    this.checkDate();
    this.applyChanges();
    this.setDateTime();
  }

  onTimeChange(eventArgs) {
    this._timeEnteredByUser = true;
    setTimeout(() => {
      // No date picked yet, or the time is incomplete: nothing to combine.
      if (!this.ngbDate || !this.ngbTime) {
        return;
      }
      this.checkDate();
      this.applyChanges();
      this.setDateTime();
    }, 250);
  }
  checkDate() {
    let date = this.combineDateTime(this.ngbDate, this.ngbTime);

    const dstGapResult = this.timeZoneHelper.ResolveDstGap(
      {
        year: this.ngbDate.year,
        month: this.ngbDate.month,
        day: this.ngbDate.day,
        hour: this.ngbTime.hour,
        minute: this.ngbTime.minute,
        second: this.ngbTime.second,
      },
      this.effectiveTimeZone
    );
    if (dstGapResult.wasRounded) {
      const roundedTimeStr = this.caDatePie.transform(
        dstGapResult.date,
        DateDisplayType.TimeWithoutSeconds,
        true
      );
      this.toasterService.warn('::DstGapTimeRounded', '', {
        messageLocalizationParams: [roundedTimeStr],
      });
      date = dstGapResult.date;
      this.ngbDate = this.getNgbStructDate(date);
      this.ngbTime = this.getNgbTimeStruct(date);
    }

    if (this.minDate && date < this.minDate) {
      this.ngbTime = this.getNgbTimeStruct(this.minDate);
      this._timeEnteredByUser = false;
    } else if (this.maxDate && date > this.maxDate) {
      this.ngbTime = this.getNgbTimeStruct(this.maxDate);
      this._timeEnteredByUser = false;
    }
  }
  getNgbStructDate(date: Date): NgbDateStruct {
    if (typeof date === 'string') {
      date = new Date(date);
    }
    const zonedComponents = this.timeZoneHelper.GetZonedDateComponents(
      date,
      this.effectiveTimeZone
    );
    const ngbDateStructFromZone = {
      day: zonedComponents.day,
      month: zonedComponents.month,
      year: zonedComponents.year,
      hour: zonedComponents.hour,
      minute: zonedComponents.minute,
      second: zonedComponents.second,
    };
    return ngbDateStructFromZone;
  }
  getNgbTimeStruct(date: Date): NgbTimeStruct {
    if (typeof date === 'string') {
      date = new Date(date);
    }
    const zonedComponents = this.timeZoneHelper.GetZonedDateComponents(
      date,
      this.effectiveTimeZone
    );
    return {
      hour: zonedComponents.hour,
      minute: zonedComponents.minute,
      second: zonedComponents.second,
    };
  }
  combineDateTime(date: NgbDateStruct, time: NgbTimeStruct): Date {
    if (typeof date === 'string') {
      const zonedTemp = this.timeZoneHelper.GetZonedDateComponents(
        new Date(date),
        this.effectiveTimeZone
      );
      date = {
        year: zonedTemp.year,
        month: zonedTemp.month,
        day: zonedTemp.day,
      };
    }
    if (date && time) {
      const zonedResultDate = this.timeZoneHelper.ResolveDstGap(
        {
          year: date.year,
          month: date.month,
          day: date.day,
          hour: time.hour,
          minute: time.minute,
          second: time.second,
        },
        this.effectiveTimeZone
      ).date;
      return zonedResultDate;
    } else {
      const combinedDate = this.timeZoneHelper.ResolveDstGap(
        { year: date.year, month: date.month, day: date.day, hour: 0, minute: 0, second: 0 },
        this.effectiveTimeZone
      ).date;
      return combinedDate;
    }
  }

  setDateTime() {
    const combinedForRender = this.combineDateTime(this.ngbDate, this.ngbTime);
    this.dateTime = this.caDatePie.transform(
      combinedForRender,
      DateDisplayType.DateTimeWithoutSeconds,
      true
    );
    this.navigateToDate(this.date);
  }
  applyChanges(): void {
    this.date = this.combineDateTime(this.ngbDate, this.ngbTime);
    this.onChange(this.date);
    this.changed.emit();
  }
  getTimeStr(time: number): string {
    return time < 10 ? '0' + time.toString() : time.toString();
  }

  navigateToDate(date: Date): void {
    //TODO:Workaround clear model bug;
    this.dtp.navigateTo({ year: 1900, month: 1 });
    this.dtp.navigateTo(this.getNgbStructDate(date));
  }

  setInfiniteDate(): void {
    this.dateTime = this.indefiniteLabel;
    this.date = null;
    this.ngbDate = null;
    this.ngbTime = null;
    this._timeEnteredByUser = false;
    this.onChange(null);
    this.changed.emit();
  }

  onShortcutClick(diff: number, suppressEvent: boolean = false) {
    const daysOffset = diff > 0 || diff === -1 || diff === 0 ? diff : diff + 1;
    const newDate = this.timeZoneHelper.GetZonedStartOfDay(daysOffset, this.effectiveTimeZone);

    this.selectedShortcut = diff;

    this.date = newDate;
    this.ngbDate = this.getNgbStructDate(this.date);
    this.ngbTime = this.getNgbTimeStruct(this.date);
    this._timeEnteredByUser = false;

    this.setDateTime();
    this.applyChanges();
  }

  setDate(newDate: Date) {
    this.date = newDate;
    this.ngbDate = this.getNgbStructDate(this.date);
    this.ngbTime = this.getNgbTimeStruct(this.date);
    this._timeEnteredByUser = false;

    this.setDateTime();
    this.applyChanges();
  }

  public openPicker(): void {
    if (this.dropDownPicker && !this.dropDownPicker.isOpen()) {
      this.dropDownPicker.open();
    }
  }
}
